import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70);
}

/**
 * Creates (or reuses) a public board listing for a marketplace service and
 * attaches it to the chosen category so the seller can pay the entry position.
 */
export const promoteServiceToBoard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ serviceId: z.string().uuid(), categoryId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: service, error: svcErr } = await supabase
      .from("marketplace_services")
      .select("id, title, slug, short_description, cover_url, listing_id, seller_user_id")
      .eq("id", data.serviceId)
      .maybeSingle();
    if (svcErr) throw new Error(svcErr.message);
    if (!service || service.seller_user_id !== userId) throw new Error("Service not found");

    const { data: category, error: catErr } = await supabase
      .from("categories")
      .select("id, slug")
      .eq("id", data.categoryId)
      .maybeSingle();
    if (catErr) throw new Error(catErr.message);
    if (!category) throw new Error("Board not found");

    const { data: profile } = await supabase.from("profiles").select("slug, handle").eq("id", userId).maybeSingle();
    const handle = profile?.slug ?? profile?.handle ?? null;
    const origin = process.env["PUBLIC_SITE_URL"] ?? "https://behot.now";
    const serviceUrl = handle ? `${origin}/marketplace/${handle}/${service.slug}` : `${origin}/marketplace/explore`;

    let listingId = service.listing_id ?? null;
    let listingSlug: string | null = null;

    if (listingId) {
      const { data: existing } = await supabase.from("listings").select("id, slug").eq("id", listingId).maybeSingle();
      listingSlug = existing?.slug ?? null;
      if (!listingSlug) listingId = null;
    }

    if (!listingId) {
      const { data: listing, error } = await supabase
        .from("listings")
        .insert({
          owner_user_id: userId,
          name: service.title.slice(0, 120),
          slug: `${slugify(service.title)}-${Math.random().toString(36).slice(2, 6)}`,
          listing_type: "creator",
          headline: service.short_description ?? null,
          short_description: service.short_description ?? null,
          website_url: serviceUrl,
          cover_url: service.cover_url ?? null,
          tags: [],
          socials: {},
          is_public: false,
          moderation_status: "pending" as const,
        })
        .select("id, slug")
        .single();
      if (error) throw new Error(error.message);
      listingId = listing.id;
      listingSlug = listing.slug;

      const { error: linkErr } = await supabase
        .from("listing_categories")
        .insert({ listing_id: listing.id, category_id: category.id });
      if (linkErr) throw new Error(linkErr.message);

      const { error: updErr } = await supabase
        .from("marketplace_services")
        .update({ listing_id: listing.id })
        .eq("id", service.id);
      if (updErr) throw new Error(updErr.message);
    } else {
      const { error: upErr } = await supabase
        .from("listing_categories")
        .upsert({ listing_id: listingId, category_id: category.id }, { onConflict: "listing_id,category_id" });
      if (upErr) throw new Error(upErr.message);
    }

    return { listingSlug: listingSlug!, categorySlug: category.slug };
  });
